import React from 'react'
import { SearchLg, XClose } from '@untitled-ui/icons-react'
import {
  Button,
  Group,
  Input,
  Label,
  SearchField as AriaSearchField,
  SearchFieldProps as AriaSearchFieldProps
} from 'react-aria-components'
import {
  composeTailwindRenderProps,
  groupBaseStyles,
  inputBaseStyles
} from './utils'
import { twMerge } from 'tailwind-merge'

export interface SearchFieldProps extends AriaSearchFieldProps {
  label?: string
  placeholder?: string
  infoElement?: React.ReactNode
  inputRef?: React.RefObject<HTMLInputElement | null>
  isBorderless?: boolean
  hideSearchIcon?: boolean
  groupClassName?: string
  clearButtonLabel?: string
}

function SearchField({
  label,
  infoElement,
  inputRef,
  isBorderless = false,
  hideSearchIcon = false,
  groupClassName,
  clearButtonLabel,
  ...props
}: SearchFieldProps) {
  return (
    <AriaSearchField
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        'group flex flex-col gap-1 min-w-[40px]'
      )}>
      {label && <Label>{label}</Label>}
      <Group
        className={twMerge(
          groupBaseStyles(),
          isBorderless
            ? '!border-0 rounded-none shadow-none outline-none focus:outline-none focus-visible:outline-none focus-within:!border-0 focus-within:outline-none focus-within:!outline-0 ring-0 focus:ring-0 focus-visible:ring-0 focus-within:ring-0 bg-transparent'
            : 'bg-neutral-50',
          groupClassName
        )}>
        {!hideSearchIcon && (
          <span className={'ml-2 text-neutral-500'} data-search-icon>
            <SearchLg width={20} aria-hidden />
          </span>
        )}
        <Input
          ref={inputRef}
          placeholder={props.placeholder ?? 'Search'}
          className={twMerge(
            inputBaseStyles(),
            isBorderless
              ? 'px-0 bg-transparent text-inherit placeholder:text-neutral-400 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 shadow-none focus:shadow-none'
              : 'bg-neutral-50',
            'peer min-w-0 aria-input flex flex-shrink flex-grow [&::-webkit-search-cancel-button]:hidden h-10'
          )}
        />

        {infoElement && (
          <div
            className={
              'invisible peer-placeholder-shown:visible flex flex-shrink-0 items-center mr-2'
            }>
            {infoElement}
          </div>
        )}

        <Button
          aria-label={clearButtonLabel}
          className='peer flex flex-shrink-0 mr-1 w-6 group-empty:hidden'>
          <XClose aria-hidden className='w-4 h-4' />
        </Button>
      </Group>
    </AriaSearchField>
  )
}

export default SearchField
